// client/src/coms/components/voiceAnimations.js
import React, { useLayoutEffect, useRef } from 'react';

export const VOICE_ANIMATION_OPTIONS = [
  { key: 'bars', label: 'Equaliser Bars' },
  { key: 'dots', label: 'Dot Bounce' },
  { key: 'spin', label: 'Rotating Ring' },
  { key: 'breath', label: 'Breathing Glow' },
  { key: 'flash', label: 'Flash Burst' },
  { key: 'orbit', label: 'Orbiting Dots' },
  { key: 'beam', label: 'Vertical Beam' },
  { key: 'matrix', label: 'Decoding Grid' },
  { key: 'droplets', label: 'Droplet Bob' },
  { key: 'blackhole', label: 'Black Hole Lens' },
  { key: 'uiversebars', label: 'Uiverse Colour Bars' },
  { key: 'dotspinner', label: 'Dot Spinner Ring' },
  { key: 'wavemenu', label: 'Wave Menu Thin' },
  { key: 'cornerspin', label: 'Corner Spin Dots' },
  { key: 'ringpulse', label: 'Ripple Circle Chain' },
  { key: 'jumpballs', label: 'Jumping Balls' },
  { key: 'eightball', label: 'Eight Ball Spin' },
  { key: 'dicegrid', label: 'Dice Grid Loader' },
  { key: 'auroraring', label: 'Aurora Ring' },
  { key: 'glassripples', label: 'Glass Ripples' },
  { key: 'zigzagballs', label: 'Zigzag Balls Vertical' },
  { key: 'pacmansolo', label: 'Pac-Man Solo' },
  { key: 'jumpdots', label: 'Jump Dots Wave' },
  { key: 'blurhalo', label: 'Blur Halo Ring' },
  { key: 'triobits', label: 'Orbit Trio Dots' },
  { key: 'hamsterwheel', label: 'Hamster Wheel' },
  { key: 'neonglow', label: 'Neon Glow Orb' },
  { key: 'radarsweep', label: 'Radar Sweep Orb' },
  { key: 'pixelghost', label: 'Pixel Ghost' },
  { key: 'centerlineeq', label: 'Center Line Equalizer' },
  { key: 'redthreebars', label: 'KITT' },
];

export const DEFAULT_VOICE_ANIMATION = 'flash';

export function getVoiceAnimationLabel(key) {
  const found = VOICE_ANIMATION_OPTIONS.find((item) => item.key === String(key || '').trim());
  return found ? found.label : 'Flash Burst';
}

export function isVoiceAnimationKey(key) {
  return VOICE_ANIMATION_OPTIONS.some((item) => item.key === String(key || '').trim());
}

// Where the "still" frame of a paused animation is taken, as a share of one iteration. Mid-cycle frames show
// every animation in a recognisable pose (at 0 several are invisible: scaled to 0 or faded out).
const STILL_FRAME_AT = 0.4;

// Freezes every animation inside `el` on a representative frame (reduced motion) or lets them run again.
export function setAnimationsStill(el, still) {
  if (!el || typeof el.getAnimations !== 'function') return 0;
  const list = el.getAnimations({ subtree: true });
  for (const a of list) {
    try {
      if (still) {
        const timing = a.effect && a.effect.getComputedTiming ? a.effect.getComputedTiming() : null;
        const d = timing && Number(timing.duration);
        a.pause();
        if (d && Number.isFinite(d)) a.currentTime = d * STILL_FRAME_AT;
      } else if (a.playState === 'paused') {
        a.play();
      }
    } catch {}
  }
  return list.length;
}

// Every speaking animation is drawn inside this stage. The app-wide "reduce motion" rule skips it (see
// personalisation.css), so the animation library always renders; `still` instead pauses it on one frame.
// That is what reduced motion looks like for speaking indicators: the indicator still appears and disappears
// with speech, it just does not move. Light themes get a dark plate so white animations stay visible.
export function AnimationStage({ type, still = false, className = '', style, children }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setAnimationsStill(el, still);
    if (!still) return undefined;
    // Animations with a start delay are created a frame later; catch those too.
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => setAnimationsStill(el, true)) : 0;
    return () => {
      if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    };
  }, [still, type]);
  return (
    <span ref={ref} className={`vx-anim-host${className ? ` ${className}` : ''}`} data-still={still ? '1' : '0'} data-animation={type} style={style}>
      {children || <IndicatorDemo type={type} />}
    </span>
  );
}

function randomFloat(seed, min, max) {
  const x = Math.sin(seed * 999.91) * 10000;
  const r = x - Math.floor(x);
  return min + r * (max - min);
}

function absFill(extra = {}) {
  return {
    position: 'absolute',
    inset: 0,
    ...extra,
  };
}

function centerAbs(extra = {}) {
  return {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    ...extra,
  };
}

function flexCenter(extra = {}) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...extra,
  };
}

export function VoiceAnimationStyles() {
  return (
    <style>{`
      @property --l {
        syntax: '<number>';
        inherits: false;
        initial-value: 0.4;
      }

      @keyframes bar-bounce {0%,100%{transform:scaleY(.3);}50%{transform:scaleY(1);}}
      @keyframes dot-bounce {0%,80%,100%{transform:translateY(0);opacity:.4;}40%{transform:translateY(-8px);opacity:1;}}
      @keyframes spin-ring {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}
      @keyframes breathe {0%,100%{transform:scale(0.5);opacity:.6;}50%{transform:scale(1.2);opacity:1;}}
      @keyframes flash {0%,100%{opacity:.2;}50%{opacity:1;}}
      @keyframes orbit {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}
      @keyframes beam {0%,100%{height:10px;opacity:.4;}50%{height:40px;opacity:1;}}
      @keyframes uiverse-scale {0%,40%,100%{transform:scaleY(0.05);}20%{transform:scaleY(1);}}
      @keyframes dot-spinner-pulse {0%,100%{transform:scale(0);opacity:0.5;}50%{transform:scale(1);opacity:1;}}
      @keyframes wave-menu-1 {from{transform:scaleY(1);}to{transform:scaleY(0.45);}}
      @keyframes wave-menu-2 {from{transform:scaleY(0.35);}to{transform:scaleY(0.7);}}
      @keyframes wave-menu-3 {from{transform:scaleY(0.6);}to{transform:scaleY(0.9);}}
      @keyframes wave-menu-4 {from{transform:scaleY(0.25);}to{transform:scaleY(0.55);}}
      @keyframes grid-float {0%{transform:translate(0,0);opacity:.6;}50%{transform:translate(var(--dx),var(--dy));opacity:1;}100%{transform:translate(0,0);opacity:.6;}}
      @keyframes droplet-bob {0%,100%{transform:translateY(10px);opacity:.78;}50%{transform:translateY(-10px);opacity:1;}}
      @keyframes accretion-rotate {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}
      @keyframes lens-wobble {0%,100%{transform:scale(1);}50%{transform:scale(1.08);}}
      @keyframes corner-spin {0%{transform:scale(1) rotate(0deg);}20%,25%{transform:scale(1.3) rotate(90deg);}45%,50%{transform:scale(1) rotate(180deg);}70%,75%{transform:scale(1.3) rotate(270deg);}95%,100%{transform:scale(1) rotate(360deg);}}
      @keyframes ring-circle-keys {0%{transform:scale(1);opacity:1;}50%{transform:scale(1.5);opacity:0.5;}100%{transform:scale(1);opacity:1;}}
      @keyframes ring-dot-keys {0%{transform:translate(-50%,-50%) scale(1);}50%{transform:translate(-50%,-50%) scale(0);}100%{transform:translate(-50%,-50%) scale(1);}}
      @keyframes ring-outline-keys {0%{transform:translate(-50%,-50%) scale(0);outline:solid 20px var(--ring-color);outline-offset:0;opacity:1;}100%{transform:translate(-50%,-50%) scale(1);outline:solid 0 transparent;outline-offset:20px;opacity:0;}}
      @keyframes jump-ball-1 {0%{transform:translateY(0) scale(1,1);}10%{transform:translateY(0) scale(1.3,0.8);}11%{transform:translateY(0) scale(0.7,1.2);animation-timing-function:cubic-bezier(0,0,0.5,1);}39%{transform:translateY(-37.5px) scale(1);animation-timing-function:cubic-bezier(0,0,0.5,1);}40%{transform:translateY(-37.5px) scale(1);}41%{transform:translateY(-37.5px) scale(1);animation-timing-function:cubic-bezier(1,0,1,0);}69%{transform:translateY(0) scale(1,1);animation-timing-function:cubic-bezier(1,0,1,0);}70%{transform:translateY(0) scale(1.5,0.4);}80%{transform:translateY(0) scale(0.8,1.2);}90%{transform:translateY(0) scale(1.1,0.8);}100%{transform:translateY(0) scale(1,1);}}
      @keyframes jump-ball-2 {0%{transform:translateY(0) scale(1,1);}10%{transform:translateY(0) scale(1.3,0.8);}11%{transform:translateY(0) scale(0.7,1.2);animation-timing-function:cubic-bezier(0,0.5,0.5,1);}39%{transform:translateY(-37.5px) scale(1);animation-timing-function:cubic-bezier(0,0.5,0.5,1);}40%{transform:translateY(-37.5px) scale(1);}41%{transform:translateY(-37.5px) scale(1);animation-timing-function:cubic-bezier(1,0,1,0.5);}69%{transform:translateY(0) scale(1,1);animation-timing-function:cubic-bezier(1,0,1,0.5);}70%{transform:translateY(0) scale(1.5,0.4);}80%{transform:translateY(0) scale(0.8,1.2);}90%{transform:translateY(0) scale(1.1,0.8);}100%{transform:translateY(0) scale(1,1);}}
      @keyframes jump-ball-3 {0%{transform:translateY(0) scale(1,1);}10%{transform:translateY(0) scale(1.3,0.8);}11%{transform:translateY(0) scale(0.7,1.2);animation-timing-function:cubic-bezier(0,1,0.5,1);}39%{transform:translateY(-37.5px) scale(1);animation-timing-function:cubic-bezier(0,1,0.5,1);}40%{transform:translateY(-37.5px) scale(1);}41%{transform:translateY(-37.5px) scale(1);animation-timing-function:cubic-bezier(1,0,1,1);}69%{transform:translateY(0) scale(1,1);animation-timing-function:cubic-bezier(1,0,1,1);}70%{transform:translateY(0) scale(1.5,0.4);}80%{transform:translateY(0) scale(0.8,1.2);}90%{transform:translateY(0) scale(1.1,0.8);}100%{transform:translateY(0) scale(1,1);}}
      @keyframes eight-ball-spin {0%{transform:rotate(0deg);}50%{transform:rotate(3.1415rad);}100%{transform:rotate(720deg);}}
      @keyframes dice-rotate {0%,20%{transform:rotate(0deg);}30%,40%{transform:rotate(90deg);}50%,60%{transform:rotate(180deg);}70%,80%{transform:rotate(270deg);}90%,100%{transform:rotate(360deg);}}
      @keyframes dice-move {0%,9%{background-position:-12px -15px,-12px 0px,-12px 15px,12px -15px,12px 0px,12px 15px;}10%,25%{background-position:0px -15px,-12px 0px,-12px 15px,34px -15px,12px 0px,12px 15px;}30%,45%{background-position:0px -34px,-12px -10px,-12px 12px,34px -15px,12px -10px,12px 12px;}50%,65%{background-position:0px -34px,-12px -34px,-12px 12px,34px -12px,0px -10px,12px 12px;}70%,85%{background-position:0px -34px,-12px -34px,0px 12px,34px -12px,0px -10px,34px 12px;}90%,100%{background-position:0px -34px,-12px -34px,0px 0px,34px -12px,0px 0px,34px 12px;}}
      @keyframes aurora-ring-rotate {0%{transform:rotate(90deg);}50%{transform:rotate(270deg);}100%{transform:rotate(450deg);}}
      @keyframes glass-ripple {0%{transform:scale(1);}50%{transform:scale(1.3);}100%{transform:scale(1);}}
      @keyframes ghost-bob {0%,49%{transform:translateY(0px);}50%,100%{transform:translateY(-8px);}}
      @keyframes ghost-flicker-a {0%,49%{background-color:#ef4444;}50%,100%{background-color:transparent;}}
      @keyframes ghost-flicker-b {0%,49%{background-color:transparent;}50%,100%{background-color:#ef4444;}}
      @keyframes ghost-eyes {0%,49%{transform:translateX(0px);}50%,99%{transform:translateX(8px);}100%{transform:translateX(0px);}}
      @keyframes ghost-shadow {0%,49%{opacity:0.5;}50%,100%{opacity:0.2;}}
      @keyframes zigzag-down {0%{transform:translateY(-8px);}50%{transform:translateY(8px);}100%{transform:translateY(-8px);}}
      @keyframes zigzag-up {0%{transform:translateY(8px);}50%{transform:translateY(-8px);}100%{transform:translateY(8px);}}
      @keyframes pacman-top {0%{transform:translate(-50%,-50%) rotate(0deg);}50%{transform:translate(-50%,-50%) rotate(-30deg);}100%{transform:translate(-50%,-50%) rotate(0deg);}}
      @keyframes pacman-bot {0%{transform:translate(-50%,50%) rotate(0deg);}50%{transform:translate(-50%,50%) rotate(30deg);}100%{transform:translate(-50%,50%) rotate(0deg);}}
      @keyframes jump-dots-wave {100%{background:#661e92;transform:translateY(-2rem) scale(1.9);}}
      @keyframes blur-halo-spin {from{transform:translate(-50%,-50%) rotate(0deg);}to{transform:translate(-50%,-50%) rotate(360deg);}}
      @keyframes trio-bits {25%{box-shadow:#dfdfdf -15px -15px,#dfdfdf 15px 15px;}50%{box-shadow:#dfdfdf 0px -20px,#dfdfdf 0px 20px;}75%{box-shadow:#dfdfdf 15px -15px,#dfdfdf -15px 15px;}100%{box-shadow:#dfdfdf 20px 0px,#dfdfdf -20px 0px;}}
      @keyframes hamster {from,to{transform:rotate(4deg) translate(-0.8em,1.85em);}50%{transform:rotate(0) translate(-0.8em,1.85em);}}
      @keyframes hamsterHead {from,25%,50%,75%,to{transform:rotate(0);}12.5%,37.5%,62.5%,87.5%{transform:rotate(8deg);}}
      @keyframes hamsterEye {from,90%,to{transform:scaleY(1);}95%{transform:scaleY(0);}}
      @keyframes hamsterEar {from,25%,50%,75%,to{transform:rotate(0);}12.5%,37.5%,62.5%,87.5%{transform:rotate(12deg);}}
      @keyframes hamsterBody {from,25%,50%,75%,to{transform:rotate(0);}12.5%,37.5%,62.5%,87.5%{transform:rotate(-2deg);}}
      @keyframes hamsterFRLimb {from,25%,50%,75%,to{transform:rotate(50deg) translateZ(-1px);}12.5%,37.5%,62.5%,87.5%{transform:rotate(-30deg) translateZ(-1px);}}
      @keyframes hamsterFLLimb {from,25%,50%,75%,to{transform:rotate(-30deg);}12.5%,37.5%,62.5%,87.5%{transform:rotate(50deg);}}
      @keyframes hamsterBRLimb {from,25%,50%,75%,to{transform:rotate(-60deg) translateZ(-1px);}12.5%,37.5%,62.5%,87.5%{transform:rotate(20deg) translateZ(-1px);}}
      @keyframes hamsterBLLimb {from,25%,50%,75%,to{transform:rotate(20deg);}12.5%,37.5%,62.5%,87.5%{transform:rotate(-60deg);}}
      @keyframes hamsterTail {from,25%,50%,75%,to{transform:rotate(30deg) translateZ(-1px);}12.5%,37.5%,62.5%,87.5%{transform:rotate(10deg) translateZ(-1px);}}
      @keyframes spoke {from{transform:rotate(0);}to{transform:rotate(-1turn);}}
      @keyframes neon-rotate {from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
      @keyframes radar81 {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}
      @keyframes ghost-up-down {0%,49%{transform:translateY(0px);}50%,100%{transform:translateY(-3px);}}
      @keyframes ghost-flicker-0 {0%,49%{opacity:1;}50%,100%{opacity:0;}}
      @keyframes ghost-flicker-1 {0%,49%{opacity:0;}50%,100%{opacity:1;}}
      @keyframes ghost-eyes-move {0%,49%{transform:translateX(0px);}50%,99%{transform:translateX(3px);}100%{transform:translateX(0px);}}
      @keyframes ghost-shadow-move {0%,49%{opacity:0.45;transform:translateX(-50%) scale(1);}50%,100%{opacity:0.2;transform:translateX(-50%) scale(0.92);}}
      @keyframes center-line-eq-pulse {0%,100%{transform:scaleY(0.2);opacity:.45;}50%{transform:scaleY(1);opacity:1;}}
      @keyframes lvlBars {
        0%,100% { --l: 0.25; }
        20% { --l: 0.6; }
        40% { --l: 0.4; }
        60% { --l: 0.85; }
        80% { --l: 0.55; }
      }
    `}</style>
  );
}

export function IndicatorDemo({ type }) {
  if (type === 'bars') {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              width: 8,
              height: 40,
              background: '#4ade80',
              transformOrigin: 'bottom',
              animation: 'bar-bounce .8s infinite',
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'dots') {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 12,
              height: 12,
              background: '#fde047',
              borderRadius: '9999px',
              animation: 'dot-bounce .9s infinite',
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'spin') {
    return (
      <div
        style={{
          width: 42,
          height: 42,
          border: '4px solid #fff',
          borderTop: '4px solid transparent',
          borderRadius: '9999px',
          animation: 'spin-ring 1s linear infinite',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  if (type === 'breath') {
    return (
      <div
        style={{
          width: 42,
          height: 42,
          background: '#c084fc',
          borderRadius: '9999px',
          animation: 'breathe 1.6s ease-in-out infinite',
        }}
      />
    );
  }

  if (type === 'flash') {
    return (
      <div
        style={{
          width: 42,
          height: 42,
          background: '#fff',
          borderRadius: '9999px',
          animation: 'flash .6s infinite',
        }}
      />
    );
  }

  if (type === 'orbit') {
    return (
      <div
        style={{
          position: 'relative',
          width: 42,
          height: 42,
          animation: 'orbit 2s linear infinite',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 12,
            height: 12,
            background: '#f472b6',
            borderRadius: '9999px',
          }}
        />
      </div>
    );
  }

  if (type === 'beam') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 48 }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              width: 8,
              background: '#22d3ee',
              borderRadius: '9999px',
              animation: 'beam .8s infinite',
              animationDelay: `${i * 0.08}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'matrix') {
    const cells = Array.from({ length: 16 });
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
        }}
      >
        {cells.map((_, i) => {
          const dx = `${randomFloat(i + 1, -3, 3).toFixed(2)}px`;
          const dy = `${randomFloat(i + 101, -3, 3).toFixed(2)}px`;
          const delay = `${randomFloat(i + 201, 0, 1.2).toFixed(2)}s`;
          const duration = `${randomFloat(i + 301, 0.8, 1.6).toFixed(2)}s`;
          return (
            <div
              key={i}
              style={{
                width: '10.67px',
                height: '10.67px',
                background: 'rgba(103,232,249,0.7)',
                '--dx': dx,
                '--dy': dy,
                animation: `grid-float ${duration} ease-in-out ${delay} infinite`,
              }}
            />
          );
        })}
      </div>
    );
  }

  if (type === 'droplets') {
    const trackHeights = [56, 66, 60, 70];
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 42 }}>
        {trackHeights.map((h, i) => (
          <div
            key={i}
            style={{
              position: 'relative',
              width: 7,
              height: `${Math.round(h * 0.6)}px`,
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2,
                top: 5,
                bottom: 5,
                borderRadius: '9999px',
                background: 'rgba(125,211,252,0.9)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 8,
                height: 11,
                animation: `droplet-bob ${0.9 + i * 0.07}s ease-in-out ${i * 0.08}s infinite`,
                top: 'calc(50% - 9px)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  transform: 'translateX(-50%)',
                  width: 7,
                  height: 5,
                  borderRadius: '9999px',
                  background: 'rgba(125,211,252,0.9)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: 4,
                  bottom: 4,
                  transform: 'translateX(-50%)',
                  width: 2,
                  borderRadius: '9999px',
                  background: 'rgba(125,211,252,0.9)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 0,
                  transform: 'translateX(-50%)',
                  width: 8,
                  height: 5,
                  borderRadius: '9999px',
                  background: 'rgba(125,211,252,0.9)',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'blackhole') {
    return (
      <div style={{ ...flexCenter(), position: 'relative', width: 42, height: 42 }}>
        <div
          style={{
            position: 'absolute',
            width: 32,
            height: 32,
            borderRadius: '9999px',
            background: '#000',
            zIndex: 10,
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: 36,
            height: 36,
            borderRadius: '9999px',
            background: 'conic-gradient(from 0deg, #ffffff 0deg, #ffffff 120deg, rgba(255,255,255,0.6) 200deg, rgba(255,255,255,0) 240deg, rgba(255,255,255,0) 360deg)',
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
            filter: 'blur(2px)',
            animation: 'accretion-rotate 1.4s linear infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: 31,
            height: 31,
            borderRadius: '9999px',
            background: 'radial-gradient(circle, rgba(0,0,0,1) 60%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.2) 100%)',
            animation: 'lens-wobble 2s ease-in-out infinite',
          }}
        />
      </div>
    );
  }

  if (type === 'uiversebars') {
    return (
      <div style={{ ...flexCenter({ gap: 3 }), width: 42, height: 42 }}>
        {['#4c86f9', '#49a84c', '#f6bb02', '#f6bb02', '#2196f3'].map((color, i) => (
          <span
            key={i}
            style={{
              display: 'block',
              width: 2,
              height: 21,
              transformOrigin: 'center',
              background: color,
              animation: 'uiverse-scale 0.9s ease-in-out infinite',
              animationDelay: `${-0.72 + i * 0.18}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'dotspinner') {
    return (
      <div style={{ ...flexCenter(), position: 'relative', width: 42, height: 42 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              ...absFill(),
              ...flexCenter({ alignItems: 'flex-start' }),
              transform: `rotate(${i * 45}deg)`,
            }}
          >
            <div
              style={{
                width: '20%',
                height: '20%',
                borderRadius: '9999px',
                backgroundColor: '#60A5FA',
                transform: 'scale(0)',
                opacity: 0.75,
                animation: 'dot-spinner-pulse 1s ease-in-out infinite',
                animationDelay: `${-0.875 + i * 0.125}s`,
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (type === 'wavemenu') {
    return (
      <div
        style={{
          ...flexCenter(),
          borderRadius: '9999px',
          border: '1px solid #545FE5',
          background: '#fff',
          width: 42,
          height: 22,
          padding: '0 4px',
          boxSizing: 'border-box',
        }}
      >
        {[
          { name: 'wave-menu-1', delay: '0s', duration: '0.32s' },
          { name: 'wave-menu-2', delay: '0.18s', duration: '0.36s' },
          { name: 'wave-menu-3', delay: '0.22s', duration: '0.42s' },
          { name: 'wave-menu-4', delay: '0.1s', duration: '0.3s' },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              width: 2,
              height: 14,
              margin: '0 1px',
              borderRadius: '9999px',
              background: '#545FE5',
              animationName: item.name,
              animationDuration: item.duration,
              animationIterationCount: 'infinite',
              animationDirection: 'alternate',
              animationDelay: item.delay,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'cornerspin') {
    return (
      <div style={{ position: 'relative', width: 32, height: 32, animation: 'corner-spin 2s linear infinite' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: 14, height: 14, borderRadius: '9999px', background: '#cbd5e1' }} />
        <div style={{ position: 'absolute', top: 0, right: 0, width: 14, height: 14, borderRadius: '9999px', background: '#cbd5e1' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: 14, height: 14, borderRadius: '9999px', background: '#cbd5e1' }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 14, height: 14, borderRadius: '9999px', background: '#cbd5e1' }} />
      </div>
    );
  }

  if (type === 'ringpulse') {
    const items = [0, 0.3, 0.6];
    return (
      <div
        style={{
          ...flexCenter({ gap: 2 }),
          width: 42,
          height: 42,
          '--ring-color': 'hsl(0, 0%, 87%)',
        }}
      >
        {items.map((delay, i) => (
          <div
            key={i}
            style={{
              position: 'relative',
              width: 14,
              height: 14,
              margin: 0,
              borderRadius: '9999px',
              border: '1px solid var(--ring-color)',
              backgroundColor: 'transparent',
              animation: 'ring-circle-keys 2s ease-in-out infinite',
              animationDelay: `${delay}s`,
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 10,
                height: 10,
                borderRadius: '9999px',
                backgroundColor: 'var(--ring-color)',
                animation: 'ring-dot-keys 2s ease-in-out infinite',
                animationDelay: `${delay}s`,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 14,
                height: 14,
                borderRadius: '9999px',
                animation: 'ring-outline-keys 2s ease-in-out infinite',
                animationDelay: `${0.9 + delay}s`,
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (type === 'jumpballs') {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 4, height: 42 }}>
        {[
          { color: '#99e2d0', name: 'jump-ball-1', delay: '0ms' },
          { color: '#12e0ad', name: 'jump-ball-2', delay: '100ms' },
          { color: '#16c197', name: 'jump-ball-3', delay: '200ms' },
        ].map((item, i) => (
          <div key={i} style={{ position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: 42, width: 12 }}>
            <div
              style={{
                position: 'absolute',
                width: 10,
                height: 10,
                borderRadius: '9999px',
                backgroundColor: item.color,
                transformOrigin: 'bottom',
                animationName: item.name,
                animationDuration: '1000ms',
                animationIterationCount: 'infinite',
                animationDelay: item.delay,
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (type === 'eightball') {
    return (
      <div
        style={{
          position: 'relative',
          width: 42,
          height: 42,
          borderRadius: '9999px',
          zIndex: 1,
          background: 'linear-gradient(to left, #000, #222)',
          animation: 'eight-ball-spin 1s linear infinite',
        }}
      >
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#fff',
            color: '#000',
            fontWeight: 800,
            borderRadius: '9999px',
            width: 16,
            height: 16,
            fontSize: 10,
            top: '40%',
            left: '40%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          8
        </div>
      </div>
    );
  }

  if (type === 'dicegrid') {
    return (
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 4,
          backgroundColor: '#fff',
          backgroundImage: 'radial-gradient(circle 5px, #19191a 100%, transparent 0), radial-gradient(circle 5px, #19191a 100%, transparent 0), radial-gradient(circle 5px, #19191a 100%, transparent 0), radial-gradient(circle 5px, #19191a 100%, transparent 0), radial-gradient(circle 5px, #19191a 100%, transparent 0), radial-gradient(circle 5px, #19191a 100%, transparent 0)',
          backgroundRepeat: 'no-repeat',
          animation: 'dice-move 4s linear infinite, dice-rotate 2s linear infinite',
        }}
      />
    );
  }

  if (type === 'auroraring') {
    return (
      <div
        style={{
          ...flexCenter(),
          position: 'relative',
          width: 42,
          height: 42,
          borderRadius: '9999px',
          userSelect: 'none',
          background: 'transparent',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            aspectRatio: '1 / 1',
            borderRadius: '9999px',
            background: 'conic-gradient(from 90deg, #fff 0deg, #ad5fff 120deg, #471eec 240deg, #fff 360deg)',
            animation: 'aurora-ring-rotate 2s linear infinite',
          }}
        />
      </div>
    );
  }

  if (type === 'glassripples') {
    const rings = [
      { inset: '40%', z: 99, border: 'rgba(100, 100, 100, 1)', delay: '0s' },
      { inset: '20%', z: 98, border: 'rgba(100, 100, 100, 0.6)', delay: '0.4s' },
      { inset: '0%', z: 97, border: 'rgba(100, 100, 100, 0.2)', delay: '0.8s' },
    ];

    return (
      <div style={{ position: 'relative', width: 40, aspectRatio: '1 / 1' }}>
        {rings.map((ring, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              inset: ring.inset,
              zIndex: ring.z,
              borderRadius: '9999px',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              background: 'linear-gradient(0deg, rgba(50, 50, 50, 0.2) 0%, rgba(100, 100, 100, 0.2) 100%)',
              borderTop: `1px solid ${ring.border}`,
              animation: 'glass-ripple 2s infinite ease-in-out',
              animationDelay: ring.delay,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'zigzagballs') {
    const items = [
      { dir: 'down', duration: '1s' },
      { dir: 'up', duration: '1.1s' },
      { dir: 'down', duration: '1.05s' },
      { dir: 'up', duration: '1.15s' },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, margin: '0 auto', gap: 2 }}>
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              width: 6,
              height: 6,
              minWidth: 6,
              minHeight: 6,
              margin: 0,
              borderRadius: '9999px',
              background: '#fff',
              flexShrink: 0,
              animation: `${item.dir === 'down' ? 'zigzag-down' : 'zigzag-up'} ${item.duration} infinite ease-in-out`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'pacmansolo') {
    return (
      <div style={{ position: 'relative', width: 42, height: 42 }}>
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 30,
            height: 15,
            background: '#EFF107',
            borderRadius: '100px 100px 0 0',
            transform: 'translate(-50%, -50%)',
            animation: 'pacman-top 0.5s linear infinite',
            transformOrigin: 'center bottom',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 30,
            height: 15,
            background: '#EFF107',
            borderRadius: '0 0 100px 100px',
            transform: 'translate(-50%, 50%)',
            animation: 'pacman-bot 0.5s linear infinite',
            transformOrigin: 'center top',
          }}
        />
      </div>
    );
  }

  if (type === 'jumpdots') {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', width: 42, height: 42, justifyContent: 'center' }}>
        {[0.1, 0.2, 0.3].map((delay, i) => (
          <div
            key={i}
            style={{
              width: 14,
              height: 14,
              borderRadius: '9999px',
              border: '1px solid #e8e8e8',
              background: '#16b0c1',
              animation: 'jump-dots-wave 0.8s ease-in-out infinite alternate',
              animationDelay: `${delay}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'blurhalo') {
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 42,
          height: 42,
          borderRadius: '9999px',
          transform: 'translate(-50%, -50%)',
          animation: 'blur-halo-spin 1.2s linear infinite',
          backgroundColor: '#9b59b6',
          backgroundImage: 'linear-gradient(red, yellow, blue)',
          border: '1px solid black',
        }}
      >
        <span
          style={{
            ...absFill(),
            borderRadius: '9999px',
            filter: 'blur(10px)',
            backgroundColor: '#9b59b6',
            backgroundImage: 'linear-gradient(#9b59b6, #84cdfa, #5ad1cd)',
          }}
        />
        <span
          style={{
            ...absFill(),
            borderRadius: '9999px',
            filter: 'blur(10px)',
            backgroundColor: '#9b59b6',
            backgroundImage: 'linear-gradient(#9b59b6, #84cdfa, #5ad1cd)',
          }}
        />
        <span
          style={{
            ...absFill(),
            borderRadius: '9999px',
            filter: 'blur(10px)',
            backgroundColor: '#9b59b6',
            backgroundImage: 'linear-gradient(#9b59b6, #84cdfa, #5ad1cd)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 14,
            right: 14,
            bottom: 14,
            borderRadius: '9999px',
            background: '#000',
            border: '1px solid #facc15',
          }}
        />
      </div>
    );
  }

  if (type === 'triobits') {
    return (
      <div
        style={{
          width: 10,
          aspectRatio: '1 / 1',
          borderRadius: '9999px',
          background: '#dfdfdf',
          boxShadow: '#dfdfdf -14px 0px, #dfdfdf 14px 0px',
          animation: 'trio-bits 1.2s infinite',
        }}
      />
    );
  }

  if (type === 'hamsterwheel') {
    return (
      <div
        style={{
          position: 'relative',
          width: 42,
          height: 42,
          fontSize: '3.5px',
          '--dur': '1s',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '9999px',
            background: 'radial-gradient(100% 100% at center, hsla(0,0%,60%,0) 47.8%, hsl(0,0%,60%) 48%)',
            zIndex: 2,
          }}
        />
        <div
          style={{
            position: 'absolute',
            animation: 'hamster var(--dur) ease-in-out infinite',
            top: '50%',
            left: 'calc(50% - 3.5em)',
            width: '7em',
            height: '3.75em',
            transform: 'rotate(4deg) translate(-0.8em,1.85em)',
            transformOrigin: '50% 0',
            zIndex: 1,
          }}
        >
          <div
            style={{
              position: 'absolute',
              animation: 'hamsterHead var(--dur) ease-in-out infinite',
              background: 'hsl(30,90%,55%)',
              borderRadius: '70% 30% 0 100% / 40% 25% 25% 60%',
              boxShadow: '0 -0.25em 0 hsl(30,90%,80%) inset, 0.75em -1.55em 0 hsl(30,90%,90%) inset',
              top: 0,
              left: '-1.35em',
              width: '2.75em',
              height: '2.5em',
              transformOrigin: '100% 50%',
            }}
          >
            <div
              style={{
                position: 'absolute',
                animation: 'hamsterEar var(--dur) ease-in-out infinite',
                background: 'hsl(0,90%,85%)',
                borderRadius: '50%',
                boxShadow: '-0.25em 0 hsl(30,90%,55%) inset',
                top: '-0.25em',
                right: '-0.25em',
                width: '0.75em',
                height: '0.75em',
                transformOrigin: '50% 75%',
              }}
            />
            <div
              style={{
                position: 'absolute',
                animation: 'hamsterEye var(--dur) linear infinite',
                backgroundColor: 'hsl(0,0%,0%)',
                borderRadius: '50%',
                top: '0.375em',
                left: '1.25em',
                width: '0.5em',
                height: '0.5em',
              }}
            />
            <div
              style={{
                position: 'absolute',
                background: 'hsl(0,90%,75%)',
                borderRadius: '35% 65% 85% 15% / 70% 50% 50% 30%',
                top: '0.75em',
                left: 0,
                width: '0.2em',
                height: '0.25em',
              }}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              animation: 'hamsterBody var(--dur) ease-in-out infinite',
              background: 'hsl(30,90%,90%)',
              borderRadius: '50% 30% 50% 30% / 15% 60% 40% 40%',
              boxShadow: '0.1em 0.75em 0 hsl(30,90%,55%) inset, 0.15em -0.5em 0 hsl(30,90%,80%) inset',
              top: '0.25em',
              left: '2em',
              width: '4.5em',
              height: '3em',
              transformOrigin: '17% 50%',
              transformStyle: 'preserve-3d',
            }}
          >
            <div
              style={{
                position: 'absolute',
                clipPath: 'polygon(0 0,100% 0,70% 80%,60% 100%,0% 100%,40% 80%)',
                top: '2em',
                left: '0.5em',
                width: '1em',
                height: '1.5em',
                transformOrigin: '50% 0',
                animation: 'hamsterFRLimb var(--dur) linear infinite',
                background: 'linear-gradient(hsl(30,90%,80%) 80%,hsl(0,90%,75%) 80%)',
                transform: 'rotate(15deg) translateZ(-1px)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                clipPath: 'polygon(0 0,100% 0,70% 80%,60% 100%,0% 100%,40% 80%)',
                top: '2em',
                left: '0.5em',
                width: '1em',
                height: '1.5em',
                transformOrigin: '50% 0',
                animation: 'hamsterFLLimb var(--dur) linear infinite',
                background: 'linear-gradient(hsl(30,90%,90%) 80%,hsl(0,90%,85%) 80%)',
                transform: 'rotate(15deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                borderRadius: '0.75em 0.75em 0 0',
                clipPath: 'polygon(0 0,100% 0,100% 30%,70% 90%,70% 100%,30% 100%,40% 90%,0% 30%)',
                top: '1em',
                left: '2.8em',
                width: '1.5em',
                height: '2.5em',
                transformOrigin: '50% 30%',
                animation: 'hamsterBRLimb var(--dur) linear infinite',
                background: 'linear-gradient(hsl(30,90%,80%) 90%,hsl(0,90%,75%) 90%)',
                transform: 'rotate(-25deg) translateZ(-1px)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                borderRadius: '0.75em 0.75em 0 0',
                clipPath: 'polygon(0 0,100% 0,100% 30%,70% 90%,70% 100%,30% 100%,40% 90%,0% 30%)',
                top: '1em',
                left: '2.8em',
                width: '1.5em',
                height: '2.5em',
                transformOrigin: '50% 30%',
                animation: 'hamsterBLLimb var(--dur) linear infinite',
                background: 'linear-gradient(hsl(30,90%,90%) 90%,hsl(0,90%,85%) 90%)',
                transform: 'rotate(-25deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                animation: 'hamsterTail var(--dur) linear infinite',
                background: 'hsl(0,90%,85%)',
                borderRadius: '0.25em 50% 50% 0.25em',
                boxShadow: '0 -0.2em 0 hsl(0,90%,75%) inset',
                top: '1.5em',
                right: '-0.5em',
                width: '1em',
                height: '0.5em',
                transform: 'rotate(30deg) translateZ(-1px)',
                transformOrigin: '0.25em 0.25em',
              }}
            />
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '9999px',
            animation: 'spoke var(--dur) linear infinite',
            background: 'radial-gradient(100% 100% at center,hsl(0,0%,60%) 4.8%,hsla(0,0%,60%,0) 5%), linear-gradient(hsla(0,0%,55%,0) 46.9%,hsl(0,0%,65%) 47% 52.9%,hsla(0,0%,65%,0) 53%) 50% 50% / 99% 99% no-repeat',
          }}
        />
      </div>
    );
  }

  if (type === 'neonglow') {
    return (
      <div
        style={{
          position: 'relative',
          width: 28,
          height: 28,
          borderRadius: '9999px',
          border: '3px solid #fff',
          animation: 'neon-rotate 1s linear infinite',
        }}
      />
    );
  }

  if (type === 'radarsweep') {
    return (
      <div
        style={{
          position: 'relative',
          width: 42,
          height: 42,
          borderRadius: '9999px',
          border: '1px solid #333',
          overflow: 'hidden',
          ...flexCenter(),
          background: 'transparent',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 6,
            borderRadius: '9999px',
            border: '1px dashed #444',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: 14,
            height: 14,
            borderRadius: '9999px',
            border: '1px dashed #444',
          }}
        />
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '50%',
            height: '100%',
            background: 'transparent',
            transformOrigin: 'top left',
            animation: 'radar81 2s linear infinite',
            borderTop: '1px dashed #fff',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'seagreen',
              transformOrigin: 'top left',
              transform: 'rotate(-55deg)',
              filter: 'blur(10px)',
              opacity: 0.9,
            }}
          />
        </span>
      </div>
    );
  }

  if (type === 'pixelghost') {
    const solidCells = [
      [5, 0], [6, 0], [7, 0], [8, 0],
      [3, 1], [4, 1], [5, 1], [6, 1], [7, 1], [8, 1], [9, 1], [10, 1],
      [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2], [8, 2], [9, 2], [10, 2], [11, 2],
      [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3], [8, 3], [9, 3], [10, 3], [11, 3], [12, 3],
      [1, 4], [2, 4], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4], [8, 4], [9, 4], [10, 4], [11, 4], [12, 4],
      [1, 5], [2, 5], [3, 5], [4, 5], [5, 5], [6, 5], [7, 5], [8, 5], [9, 5], [10, 5], [11, 5], [12, 5],
      [0, 6], [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 6], [7, 6], [8, 6], [9, 6], [10, 6], [11, 6], [12, 6], [13, 6],
      [0, 7], [1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7], [7, 7], [8, 7], [9, 7], [10, 7], [11, 7], [12, 7], [13, 7],
      [0, 8], [1, 8], [2, 8], [3, 8], [4, 8], [5, 8], [6, 8], [7, 8], [8, 8], [9, 8], [10, 8], [11, 8], [12, 8], [13, 8],
      [0, 9], [1, 9], [2, 9], [3, 9], [4, 9], [5, 9], [6, 9], [7, 9], [8, 9], [9, 9], [10, 9], [11, 9], [12, 9], [13, 9],
      [0, 10], [1, 10], [2, 10], [3, 10], [4, 10], [5, 10], [6, 10], [7, 10], [8, 10], [9, 10], [10, 10], [11, 10], [12, 10], [13, 10],
      [0, 11], [1, 11], [2, 11], [3, 11], [4, 11], [5, 11], [6, 11], [7, 11], [8, 11], [9, 11], [10, 11], [11, 11], [12, 11], [13, 11],
      [0, 12], [1, 12], [3, 12], [5, 12], [8, 12], [10, 12], [12, 12], [13, 12],
    ];
    const flickerA = [[0, 13], [13, 13], [5, 13], [8, 13], [6, 12], [11, 12]];
    const flickerB = [[1, 13], [2, 13], [3, 13], [4, 13], [6, 13], [7, 13], [8, 13], [9, 13], [10, 13], [11, 13], [12, 13], [2, 12], [4, 12], [9, 12], [11, 12]];

    return (
      <div style={{ position: 'relative', width: 42, height: 42 }}>
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 2,
            width: 30,
            height: 10,
            borderRadius: '9999px',
            background: '#000',
            filter: 'blur(5px)',
            animation: 'ghost-shadow-move 0.5s infinite',
            transform: 'translateX(-50%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 2,
            transform: 'translateX(-50%)',
            animation: 'ghost-up-down 0.5s infinite',
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'grid',
              width: 42,
              height: 42,
              gridTemplateColumns: 'repeat(14, 1fr)',
              gridTemplateRows: 'repeat(14, 1fr)',
              transform: 'scale(0.9)',
              transformOrigin: 'center center',
            }}
          >
            {solidCells.map(([x, y], i) => (
              <div key={`s-${i}`} style={{ gridColumn: x + 1, gridRow: y + 1, backgroundColor: 'red' }} />
            ))}
            {flickerA.map(([x, y], i) => (
              <div key={`a-${i}`} style={{ gridColumn: x + 1, gridRow: y + 1, backgroundColor: 'red', animation: 'ghost-flicker-0 0.5s infinite' }} />
            ))}
            {flickerB.map(([x, y], i) => (
              <div key={`b-${i}`} style={{ gridColumn: x + 1, gridRow: y + 1, backgroundColor: 'red', animation: 'ghost-flicker-1 0.5s infinite' }} />
            ))}
          </div>

          <div style={{ position: 'absolute', top: 9, left: 6, width: 12, height: 15 }}>
            <div style={{ position: 'absolute', left: 3, top: 0, width: 6, height: 15, background: '#fff' }} />
            <div style={{ position: 'absolute', left: 0, top: 3, width: 12, height: 9, background: '#fff' }} />
          </div>

          <div style={{ position: 'absolute', top: 9, right: 9, width: 12, height: 15 }}>
            <div style={{ position: 'absolute', left: 3, top: 0, width: 6, height: 15, background: '#fff' }} />
            <div style={{ position: 'absolute', left: 0, top: 3, width: 12, height: 9, background: '#fff' }} />
          </div>

          <div style={{ position: 'absolute', top: 15, left: 6, zIndex: 1, animation: 'ghost-eyes-move 3s infinite' }}>
            <div style={{ position: 'absolute', width: 6, height: 6, background: '#3b82f6' }} />
            <div style={{ position: 'absolute', left: 17, width: 6, height: 6, background: '#3b82f6' }} />
          </div>
        </div>
      </div>
    );
  }

  if (type === 'centerlineeq') {
    const bars = [0.32, 0.42, 0.54, 0.68, 0.84, 0.84, 0.68, 0.54, 0.42, 0.32];

    return (
      <div
        style={{
          position: 'relative',
          width: 42,
          height: 42,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: 'fit-content',
            height: 28,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 1,
              background: 'rgba(255,255,255,0.2)',
              transform: 'translateY(-50%)',
            }}
          />
          {bars.map((base, i) => {
            const duration = 0.9 + Math.abs(i - 4.5) * 0.08;
            const delay = i * 0.08;

            return (
              <div
                key={i}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  width: 2,
                  height: `${Math.round(26 * base)}px`,
                  borderRadius: '9999px',
                  background: 'rgba(255,255,255,0.92)',
                  transformOrigin: 'center',
                  animation: `center-line-eq-pulse ${duration}s ease-in-out ${delay}s infinite`,
                  boxShadow: '0 0 8px rgba(255,255,255,0.12)',
                }}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (type === 'redthreebars') {
    const segments = 14;

    return (
      <div
        style={{
          width: 42,
          height: 42,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            height: 34,
            padding: 0,
            background: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
          }}
        >
          {[
            { key: 'a', amp: 0.75 },
            { key: 'b', amp: 1.1 },
            { key: 'c', amp: 0.75 },
          ].map((bar) => (
            <div
              key={bar.key}
              style={{
                '--l': 0.5,
                '--amp': bar.amp,
                width: 8,
                height: 34,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                clipPath: 'inset(calc((1 - (var(--l) * var(--amp))) * 50%) 0 calc((1 - (var(--l) * var(--amp))) * 50%) 0)',
                animation: 'lvlBars 0.48s ease-in-out infinite',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  width: '100%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {Array.from({ length: segments }).map((_, i) => (
                  <div
                    key={`${bar.key}-${i}`}
                    style={{
                      width: '100%',
                      height: 2,
                      borderRadius: 1,
                      background: 'linear-gradient(180deg, rgba(255, 220, 150, 0.98) 0%, rgba(255, 165, 90, 0.96) 14%, rgba(255, 70, 40, 0.94) 34%, rgba(210, 0, 0, 0.8) 64%, rgba(90, 0, 0, 0.3) 84%, rgba(0, 0, 0, 0) 100%)',
                      boxShadow: '0 0 4px rgba(255, 160, 80, 0.32), 0 0 6px rgba(255, 50, 50, 0.24), 0 0 8px rgba(255, 0, 0, 0.14), inset 0 1px 0 rgba(255,255,255,0.18)',
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}